<script setup lang="ts">
import type { AppTextareaEvent } from '@shared/ui/fields'
import type {
  DisciplineProgressReportStatementKey,
  DisciplineProgressReportStatementsErrors,
  DisciplineProgressReportStatementsForm,
} from '../model/types'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import { AppTextarea } from '@shared/ui/fields'

import { hasStatementErrors } from '../model/discipline-progress-reports'

defineOptions({
  name: 'DisciplineProgressReportSection',
})

const props = withDefaults(defineProps<{
  title: string
  open: boolean
  statements: DisciplineProgressReportStatementsForm
  errors: DisciplineProgressReportStatementsErrors
  disabled?: boolean
}>(), {
  disabled: false,
})

const emit = defineEmits<{
  'click:toggle': []
  'update:statement': [DisciplineProgressReportStatementKey, string]
}>()

const FIELDS: readonly { key: DisciplineProgressReportStatementKey, label: string, required: boolean }[] = [
  { key: 'statement_1', label: 'Statement 1', required: true },
  { key: 'statement_2', label: 'Statement 2', required: true },
  { key: 'statement_3', label: 'Statement 3', required: true },
  { key: 'statement_4', label: 'Statement 4 (Optional)', required: false },
]

const REQUIRED_MESSAGE = 'Field is required'

const refIsInvalid = computed<boolean>(() => hasStatementErrors(props.errors))
</script>

<template>
  <div class="discipline-progress-report-section">
    <div :class="['discipline-progress-report-section__wrapper', { error: refIsInvalid }]">
      <button
        type="button"
        class="discipline-progress-report-section__toggle"
        :aria-expanded="open"
        @click="emit('click:toggle')"
      >
        <AppText name="body2-medium">
          {{ title }}
        </AppText>
        <AppIcon
          :icon="open ? faChevronUp : faChevronDown"
          size="small"
        />
      </button>
      <div
        v-if="open"
        class="discipline-progress-report-section__inner"
      >
        <AppTextarea
          v-for="field in FIELDS"
          :key="field.key"
          :label="field.label"
          placeholder="Type here"
          :required="field.required"
          :value="statements[field.key]"
          :disabled="disabled"
          :mode="errors[field.key] ? 'error' : 'normal'"
          :error-text="REQUIRED_MESSAGE"
          @input="(event: AppTextareaEvent) => emit('update:statement', field.key, event.value)"
        />
      </div>
    </div>
    <AppText
      v-if="refIsInvalid"
      name="body2"
      color="red-500"
    >
      Please fill in all required fields in this section
    </AppText>
  </div>
</template>

<style scoped lang="scss" src="./DisciplineProgressReportSection.scss" />
